import React, { FC } from "react";

import "../styles.scss";
import "../scripts";

import {
  primaryBlock,
  PrimaryBlockConfig,
  getDefaultProps,
  KameleonElement,
  SecondaryBlockSize,
  TertiaryBlockSize,
  BlockTheme,
  BlockType,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";

import { webflowHOC } from "@kameleon-common/webflow-js";
import { Variants } from "@kameleon-util/global-theme";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import { ContentType, layoutOptions } from "./types/prop-types";

/** Content Primary */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.None,
  tertiaryBlockSize: TertiaryBlockSize.None,
  blockType: BlockType.Primary,
  blockTheme: BlockTheme.light,
};

const ContentComponent: FC<ContentType> = ({
  items,
  tagline,
  header,
  description,
  layoutProps,
}: ContentType) => {
  const textIsCenter = layoutProps.textAlignment === "center";
  return (
    <Box className="primary-content-three-tabs-large-media-v1 section_layout406">
      <Box className="padding-global">
        <Box className="container-large padding-section-large">
          {(header || description) && (
            <Box className="margin-bottom margin-xxlarge">
              <Box
                className={`max-width-large ${textIsCenter && "align-center"}`}
              >
                {tagline && (
                  <Box className="margin-bottom margin-xsmall">
                    <Variants.TagLine>{tagline}</Variants.TagLine>
                  </Box>
                )}
                <Box className="margin-bottom margin-small">
                  <Variants.Heading>{header}</Variants.Heading>
                </Box>
                <Variants.ParagraphMedium>
                  {description}
                </Variants.ParagraphMedium>
              </Box>
            </Box>
          )}
          <Box
            data-current="Tab 1"
            data-easing="ease"
            data-duration-in="300"
            data-duration-out="100"
            className="layout406_component w-tabs"
          >
            <Box className="layout406_tabs-menu w-tab-menu">
              {items.map((item, i) => (
                <Variants.LinkTab
                  key={i}
                  data-w-tab={`Tab ${i + 1}`}
                  className="layout406_tab-link w-inline-block w-tab-link w--current"
                >
                  <Variants.Heading as="h6">{item.title}</Variants.Heading>
                  <Variants.ParagraphDefault>
                    {item.description}
                  </Variants.ParagraphDefault>
                </Variants.LinkTab>
              ))}
            </Box>
            <Box className="layout406_tabs-content w-tab-content">
              {items.map((item, i) => (
                <Box
                  key={i}
                  data-w-tab={`Tab ${i + 1}`}
                  className="layout406_tab-pane w-tab-pane w--tab-active"
                >
                  <Box className="layout406_tab-wrapper">
                    <Box className="layout406_image-wrapper">
                      <Variants.Image
                        alt={item.image.alt}
                        loading="lazy"
                        src={item.image.url}
                        className="layout406_image"
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC(ContentComponent, ["tabs"]);

export const Content = primaryBlock<ContentType>(
  blockConfig,
  ComponentWithWebflow
);
Content.defaultProps = getDefaultProps<ContentType>(defaults);

export const Element: KameleonElement = {
  component: Content,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
